"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api/v1";

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail || res.statusText);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Try to restore session from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("access_token");
    if (!stored) {
      setLoading(false);
      return;
    }
    setToken(stored);
    // Validate token by fetching /auth/me
    apiFetch<AuthUser>("/auth/me", {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then((u) => {
        setUser(u);
        setLoading(false);
      })
      .catch(() => {
        // Token expired — try refresh
        const refresh = localStorage.getItem("refresh_token");
        if (!refresh) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          setToken(null);
          setLoading(false);
          return;
        }
        apiFetch<{ access_token: string; refresh_token: string }>("/auth/refresh", {
          method: "POST",
          body: JSON.stringify({ refresh_token: refresh }),
        })
          .then((tokens) => {
            localStorage.setItem("access_token", tokens.access_token);
            localStorage.setItem("refresh_token", tokens.refresh_token);
            setToken(tokens.access_token);
            return apiFetch<AuthUser>("/auth/me", {
              headers: { Authorization: `Bearer ${tokens.access_token}` },
            });
          })
          .then((u) => {
            setUser(u);
            setLoading(false);
          })
          .catch(() => {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            setToken(null);
            setLoading(false);
          });
      });
  }, []);

  // Detect when tokens are cleared by api-client (e.g. expired session)
  // and update auth state without a hard page reload
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem("access_token");
      if (!stored && token) {
        // Token was cleared externally (by api-client 401 handler)
        setToken(null);
        setUser(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiFetch<{
      access_token: string;
      refresh_token: string;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    localStorage.setItem("access_token", result.access_token);
    localStorage.setItem("refresh_token", result.refresh_token);
    setToken(result.access_token);

    const u = await apiFetch<AuthUser>("/auth/me", {
      headers: { Authorization: `Bearer ${result.access_token}` },
    });
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, login, logout }),
    [user, token, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
